import { ALTERSVERGLEICH, OPT_VERMOEGEN, SHOW_ALTERSVERGLEICH_WERTE, VermoegensPosten } from "../state/schema";
import {
  card, h, jaNein, moneyInput, note, objText, objTextarea, selectInput, textInput, textareaInput,
} from "../components/ui";
import { uploadBlock } from "../components/upload";
import {
  berechneHaushalt, gehaltszahlungenProJahr, summeSparbeitraege, summeVersicherungsbeitraege,
} from "../logic/calc";
import { ageFromISO, amountToInput, formatEUR, parseAmount } from "../logic/format";
import {
  vAusgaben, vEinkommen, vGas, vKrankenversicherung, vStrom, vVermoegen,
} from "../logic/validation";
import { Step } from "./types";

const newId = () => "p-" + Math.random().toString(36).slice(2, 10);

/* ---------- 9 · Vermögen & Rücklagen ---------- */
export const stepVermoegen: Step = {
  id: "vermoegen",
  section: "vermoegen",
  eyebrow: "Vermögen",
  title: "Das hast du dir bisher aufgebaut",
  subtitle: "Ein Überblick über deine Werte und Rücklagen.",
  validate: vVermoegen,
  build(ctx) {
    const s = ctx.store;
    const p = () => s.persist();
    const chips = h("div", { class: "chips" });
    const cardsWrap = h("div");

    const ensure = () => {
      const sel: string[] = s.state.vermoegen.posten.map((x) => x.kategorie);
      return sel;
    };

    const toggle = (kat: string) => {
      s.update((st) => {
        const i = st.vermoegen.posten.findIndex((x) => x.kategorie === kat);
        if (i >= 0) st.vermoegen.posten.splice(i, 1);
        else st.vermoegen.posten.push(emptyPosten(kat));
      }, false);
      renderChips();
      renderCards();
    };

    const renderChips = () => {
      chips.innerHTML = "";
      const sel = ensure();
      OPT_VERMOEGEN.forEach((kat) => {
        const active = sel.includes(kat);
        chips.appendChild(h("button", { type: "button", class: "chip", text: kat, attrs: { "aria-pressed": String(active) }, on: { click: () => toggle(kat) } }));
      });
    };

    const renderCards = () => {
      cardsWrap.innerHTML = "";
      s.state.vermoegen.posten.forEach((v) => cardsWrap.appendChild(renderPostenCard(v, p)));
    };

    const detail = h("div");
    const renderDetail = () => {
      detail.innerHTML = "";
      if (s.get("vermoegen.vorhanden") !== "ja") return;
      detail.append(
        card(h("div", { class: "card__title", text: "Welche Kategorien hast du?" }),
          h("p", { class: "card__hint", text: "Wähle alles aus, was auf dich zutrifft." }), chips),
        cardsWrap,
        altersvergleichCard(s)
      );
      renderChips();
      renderCards();
    };
    renderDetail();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "Hast du bereits Vermögenswerte oder Rücklagen aufgebaut? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "vermoegen.vorhanden", renderDetail, true)),
      detail);
  },
};

function emptyPosten(kategorie: string): VermoegensPosten {
  return { id: newId(), kategorie, wert: "", wertUnbekannt: false, sparbeitrag: "", anbieter: "", notiz: "" };
}

function renderPostenCard(v: VermoegensPosten, p: () => void): HTMLElement {
  const body = h("div");
  const draw = () => {
    body.innerHTML = "";
    const id = "wt-" + v.id;
    const toggle = h("div", { class: "consent", style: { padding: "4px 0" } },
      h("input", { id, type: "checkbox", checked: v.wertUnbekannt,
        on: { change: (e: Event) => { v.wertUnbekannt = (e.target as HTMLInputElement).checked; p(); draw(); } } }),
      h("label", { for: id, text: "Wert weiß ich nicht" }));
    body.append(
      objText(v, "wert", p, { label: "Ungefährer aktueller Wert", money: true, disabled: v.wertUnbekannt }),
      toggle,
      h("div", { class: "grid-2" },
        objText(v, "sparbeitrag", p, { label: "Monatlicher Sparbeitrag", money: true, optional: true }),
        objText(v, "anbieter", p, { label: "Anbieter", optional: true })),
      objTextarea(v, "notiz", p, { label: "Notiz", optional: true, rows: 2 })
    );
  };
  draw();
  return h("div", { class: "subcard" }, h("div", { class: "subcard__title", style: { marginBottom: "10px" }, text: v.kategorie }), body);
}

function altersvergleichCard(s: any): HTMLElement {
  // Bis zur Quellenprüfung (Brief §14) keine konkreten Zahlen veröffentlichen.
  if (!SHOW_ALTERSVERGLEICH_WERTE) {
    return card(
      h("div", { class: "card__title", text: "Orientierungswert deiner Altersgruppe" }),
      note("info", "Einen belastbaren Vergleichswert für deine Altersgruppe ergänzen wir, sobald die Datengrundlage geprüft ist. Entscheidend ist ohnehin, dass du regelmäßig für deine Ziele zurücklegst – dein Berater ordnet deine Situation persönlich ein.")
    );
  }
  const age = ageFromISO(s.get("stammdaten.geburtsdatum"));
  const rows = ALTERSVERGLEICH.map((r) => {
    const [from, to] = r.range.split(" bis ").map((x) => parseInt(x, 10));
    const active = age != null && age >= from && age <= to;
    return h("div", {
      class: "kv__row",
      style: active ? { background: "rgba(55,196,241,0.12)", borderRadius: "8px", padding: "8px 10px" } : {},
    },
      h("span", { class: "kv__k", style: { color: "var(--on-card-muted)" }, text: r.range + " Jahre" }),
      h("span", { class: "kv__v", style: { color: "var(--on-card)" }, text: formatEUR(r.wert) }));
  });
  return card(
    h("div", { class: "card__title", text: "Orientierungswert deiner Altersgruppe" }),
    h("div", { class: "kv" }, ...rows),
    h("p", { class: "field__optional", text: "Quelle: Bundeszentrale für politische Bildung." }),
    note("info", "Dieser Wert ist nur eine statistische Orientierung und keine persönliche Zielvorgabe.")
  );
}

/* ---------- 10 · Strom ---------- */
export const stepStrom: Step = {
  id: "strom",
  section: "haushalt",
  eyebrow: "Haushaltscheck",
  title: "Strom",
  subtitle: "Deine aktuelle Stromversorgung.",
  validate: vStrom,
  build(ctx) {
    const s = ctx.store;
    const detail = h("div");
    const render = () => {
      detail.innerHTML = "";
      if (s.get("strom.eigenerVertrag") !== "ja") return;
      detail.appendChild(card(
        h("div", { class: "grid-2" },
          textInput(s, "strom.anbieter", { label: "Stromanbieter", required: true }),
          moneyInput(s, "strom.abschlag", { label: "Monatlicher Abschlag", required: true })),
        h("div", { class: "grid-2" },
          textInput(s, "strom.vertragsende", { label: "Vertragsende", optional: true }),
          textInput(s, "strom.kuendigungsfrist", { label: "Kündigungsfrist", optional: true })),
        h("div", { class: "field" },
          h("div", { class: "field__label", html: "Letzte Jahresabrechnung <span style='color:var(--neg)'>*</span>" }),
          h("p", { class: "field__optional", text: "Bitte lade deine letzte Stromabrechnung hoch – sie ist für die Analyse erforderlich." }),
          uploadBlock(s, { kategorie: "Stromabrechnung", onChange: () => ctx.store.emit() }))
      ));
    };
    render();
    return h("div", {},
      card(h("div", { class: "card__title", html: "Beziehst du aktuell Strom über einen eigenen Vertrag? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "strom.eigenerVertrag", render, true)),
      detail);
  },
};

/* ---------- 11 · Gas ---------- */
export const stepGas: Step = {
  id: "gas",
  section: "haushalt",
  eyebrow: "Haushaltscheck",
  title: "Gas",
  subtitle: "Falls du mit Gas heizt oder kochst.",
  validate: vGas,
  build(ctx) {
    const s = ctx.store;
    const detail = h("div");
    const render = () => {
      detail.innerHTML = "";
      if (s.get("gas.eigenerVertrag") !== "ja") return;
      detail.appendChild(card(
        h("div", { class: "grid-2" },
          textInput(s, "gas.anbieter", { label: "Gasanbieter", required: true }),
          moneyInput(s, "gas.abschlag", { label: "Monatlicher Abschlag", required: true })),
        h("div", { class: "grid-2" },
          textInput(s, "gas.vertragsende", { label: "Vertragsende", optional: true }),
          textInput(s, "gas.kuendigungsfrist", { label: "Kündigungsfrist", optional: true })),
        h("div", { class: "field" },
          h("div", { class: "field__label", html: "Letzte Jahresabrechnung <span style='color:var(--neg)'>*</span>" }),
          h("p", { class: "field__optional", text: "Bitte lade deine letzte Gasabrechnung hoch – sie ist für die Analyse erforderlich." }),
          uploadBlock(s, { kategorie: "Gasabrechnung", onChange: () => ctx.store.emit() }))
      ));
    };
    render();
    return h("div", {},
      card(h("div", { class: "card__title", html: "Beziehst du aktuell Gas über einen eigenen Vertrag? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "gas.eigenerVertrag", render, true),
        note("info", 'Mit Fernwärme, Zentralheizung oder in der Miete enthaltenen Heizkosten kannst du „Nein" wählen.')),
      detail);
  },
};

/* ---------- 11b · Gesetzliche Krankenversicherung ---------- */
export const stepKrankenversicherung: Step = {
  id: "krankenversicherung",
  section: "haushalt",
  eyebrow: "Haushaltscheck",
  title: "Krankenversicherung",
  subtitle: "Deine gesetzliche Krankenkasse.",
  validate: vKrankenversicherung,
  build(ctx) {
    const s = ctx.store;
    const detail = h("div");

    const bonusDetail = h("div");
    const renderBonus = () => {
      bonusDetail.innerHTML = "";
      if (s.get("krankenversicherung.bonusprogramme") === "ja")
        bonusDetail.appendChild(h("div", { style: { marginTop: "10px" } },
          textareaInput(s, "krankenversicherung.bonusprogrammeText", {
            label: "Welche Bonusprogramme nutzt du?", optional: true, rows: 2,
            placeholder: "z. B. Vorsorge-Bonus, Sport, Gesundheitskurse …",
          })));
    };

    const render = () => {
      detail.innerHTML = "";
      if (s.get("krankenversicherung.gesetzlichVersichert") !== "ja") return;
      detail.append(
        card(
          h("div", { class: "card__title", text: "Deine Krankenkasse" }),
          textInput(s, "krankenversicherung.anbieter", { label: "Name deiner Krankenkasse", required: true, placeholder: "z. B. TK, AOK, Barmer, DAK …" })
        ),
        card(
          h("div", { class: "card__title", text: "Bonusprogramme" }),
          h("p", { class: "card__hint", text: "Viele Krankenkassen belohnen z. B. Vorsorge oder Sport mit Bonusprogrammen." }),
          jaNein(s, "krankenversicherung.bonusprogramme", renderBonus),
          bonusDetail
        ),
        card(
          h("div", { class: "card__title", html: "Was hält dich aktuell bei deiner Krankenkasse? <span style='color:var(--neg)'>*</span>" }),
          textareaInput(s, "krankenversicherung.halt", {
            label: "Deine Antwort", required: true, rows: 2,
            placeholder: "z. B. guter Service, Zusatzleistungen, Gewohnheit, nichts Bestimmtes …",
          })
        )
      );
      renderBonus();
    };
    render();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "Bist du gesetzlich krankenversichert? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "krankenversicherung.gesetzlichVersichert", render),
        note("info", 'Privat- oder familienversicherte Personen können „Nein" wählen.')),
      detail);
  },
};

/* ---------- 12 · Einkommen + Nachweis ---------- */
export const stepEinkommen: Step = {
  id: "einkommen",
  section: "haushalt",
  eyebrow: "Haushaltscheck",
  title: "Einkommen",
  subtitle: "Alle Beträge bitte als monatliche Werte.",
  leaveMessage: "Jetzt schauen wir auf deine Ausgangslage.",
  validate: vEinkommen,
  build(ctx) {
    const s = ctx.store;
    const avgLine = h("p", { class: "field__optional" });
    const updateAvg = () => {
      const zahlungen = gehaltszahlungenProJahr(s.state);
      const netto = parseAmount(s.get("einkommen.nettoMonat"));
      const zusatz = netto * (Math.max(zahlungen - 12, 0) / 12);
      avgLine.textContent = zahlungen > 12 && netto > 0
        ? `Zusätzliche Gehaltszahlungen entsprechen rund ${formatEUR(zusatz)} pro Monat (automatisch umgerechnet).`
        : "";
    };

    const andereWrap = h("div");
    const renderAndere = () => {
      andereWrap.innerHTML = "";
      if (s.get("einkommen.gehaltszahlungen") === "andere")
        andereWrap.appendChild(textInput(s, "einkommen.gehaltszahlungenAndere", { label: "Anzahl der Gehaltszahlungen", required: true, type: "number", inputmode: "numeric", onInput: updateAvg }));
      updateAvg();
    };

    const gehaltSelect = selectInput(s, "einkommen.gehaltszahlungen", ["12", "13", "14", "andere"], { label: "Anzahl der Gehaltszahlungen pro Jahr", required: true, onInput: renderAndere });

    // Nachweis
    const nachweisDetail = h("div");
    const renderNachweis = () => {
      nachweisDetail.innerHTML = "";
      const art = s.get("einkommensnachweis.art");
      if (art === "jahresendabrechnung" || art === "gehaltsabrechnung") {
        nachweisDetail.append(
          note("info", "Bitte mindestens eine Datei hochladen – das ist für die Analyse erforderlich."),
          uploadBlock(s, { kategorie: "Einkommensnachweis", onChange: () => ctx.store.emit() }));
      } else if (art === "keine") {
        nachweisDetail.append(
          textareaInput(s, "einkommensnachweis.begruendung", {
            label: "Warum steht dir aktuell keine Gehaltsabrechnung zur Verfügung?", required: true, rows: 3,
            placeholder: "z. B. selbstständig, freiberuflich, aktuell ohne Beschäftigung, keine Abrechnung verfügbar, sonstiger Grund",
          }),
          note("info", "Die Einnahmen- und Ausgabenrechnung bleibt trotzdem erforderlich."));
      }
    };

    const artCards = h("div", { class: "options" });
    const nachweisOpts = [
      { value: "jahresendabrechnung", title: "Letzte Jahresendabrechnung des Arbeitgebers" },
      { value: "gehaltsabrechnung", title: "Aktuelle Gehaltsabrechnung" },
      { value: "keine", title: "Ich habe keine Gehaltsabrechnung" },
    ];
    const drawArt = () => {
      artCards.innerHTML = "";
      nachweisOpts.forEach((o) => {
        artCards.appendChild(h("button", { type: "button", class: "opt", attrs: { "aria-pressed": String(s.get("einkommensnachweis.art") === o.value) },
          on: { click: () => { s.set("einkommensnachweis.art", o.value); drawArt(); renderNachweis(); } } },
          h("span", { class: "opt__title", text: o.title })));
      });
    };
    drawArt();
    renderNachweis();
    renderAndere();

    return h("div", {},
      card(
        h("div", { class: "card__title", text: "Dein Einkommen" }),
        h("div", { class: "grid-2" },
          moneyInput(s, "einkommen.bruttoMonat", { label: "Monatliches Bruttoeinkommen", required: true, hint: "Dient nur als Information und wird nicht zur verfügbaren Summe addiert." }),
          moneyInput(s, "einkommen.nettoMonat", { label: "Monatliches Nettoeinkommen", required: true, onInput: updateAvg })),
        gehaltSelect,
        andereWrap,
        avgLine,
        h("div", { class: "grid-3" },
          moneyInput(s, "einkommen.nebeneinkuenfte", { label: "Nebeneinkünfte", required: true }),
          moneyInput(s, "einkommen.kindergeld", { label: "Kindergeld", required: true }),
          moneyInput(s, "einkommen.weitereEinnahmen", { label: "Weitere verfügbare Einnahmen", required: true })),
        note("info", "Nicht vorhandene Einnahmen bitte mit 0 € eintragen.")
      ),
      card(
        h("div", { class: "card__title", html: "Welchen Einkommensnachweis möchtest du hochladen? <span style='color:var(--neg)'>*</span>" }),
        artCards,
        nachweisDetail
      )
    );
  },
};

/* ---------- 13 · Ausgaben ---------- */
export const stepAusgaben: Step = {
  id: "ausgaben",
  section: "haushalt",
  eyebrow: "Haushaltscheck",
  title: "Ausgaben",
  subtitle: "Bitte jedes Feld ausfüllen – 0 € ist möglich.",
  validate: vAusgaben,
  build(ctx) {
    const s = ctx.store;

    const postenSumme = summeSparbeitraege(s.state);
    const versSumme = summeVersicherungsbeitraege(s.state);
    // Auto-Übernahme: die Felder dürfen nie unter der oben erfassten Summe liegen
    if (postenSumme > 0 && parseAmount(s.get("ausgaben.sparbeitraege")) < postenSumme - 0.005)
      s.set("ausgaben.sparbeitraege", amountToInput(postenSumme));
    if (versSumme > 0 && parseAmount(s.get("ausgaben.versicherungen")) < versSumme - 0.005)
      s.set("ausgaben.versicherungen", amountToInput(versSumme));

    // --- Versicherungsbeiträge: Auto-Übernahme (AV+AKS+Sach) + Mehrbetrag-Pflichtfrage ---
    const versSurplusBox = h("div");
    const renderVersSurplus = () => {
      versSurplusBox.innerHTML = "";
      const extra = parseAmount(s.get("ausgaben.versicherungen")) - versSumme;
      if (extra > 0.005) versSurplusBox.append(
        note("info-dark", `Das sind ${formatEUR(extra)} mehr als deine oben erfassten Verträge (${formatEUR(versSumme)}).`),
        textareaInput(s, "ausgaben.versZielText", { label: "Welche zusätzlichen Versicherungen deckt dieser höhere Beitrag ab?", required: true, rows: 2, placeholder: "z. B. private Krankenzusatz, Risikoleben, weitere Sachversicherung …" }),
        h("button", { type: "button", class: "btn-edit", text: "Weiteren Vertrag oben ergänzen", on: { click: () => ctx.goTo("altersvorsorge") } }),
      );
    };
    const versHint = "Monatliche Beiträge für deine Versicherungen. Wird eine Versicherung jährlich oder quartalsweise abgerechnet, rechne sie auf den Monat um (z. B. Jahresbeitrag ÷ 12) und zähle sie hinzu.";
    const versUebernahme = versSumme > 0
      ? h("p", { class: "field__optional", text: `Aus deinen oben erfassten Verträgen (Altersvorsorge, Arbeitskraft, Sachversicherungen) übernehmen wir bereits ${formatEUR(versSumme)}. Zahlst du insgesamt mehr, trag den Gesamtbetrag ein.` })
      : null;

    // --- Bestehende Sparbeiträge: Auto-Übernahme (Vermögens-Posten) + Mehrbetrag-Pflichtfrage ---
    const sparSurplusBox = h("div");
    const renderSparSurplus = () => {
      sparSurplusBox.innerHTML = "";
      const extra = parseAmount(s.get("ausgaben.sparbeitraege")) - postenSumme;
      if (extra > 0.005) sparSurplusBox.append(
        note("info-dark", `Du sparst ${formatEUR(extra)} mehr, als in deinen oben erfassten Sparprodukten (${formatEUR(postenSumme)}) hinterlegt ist.`),
        textareaInput(s, "ausgaben.sparZielText", { label: "Wohin fließt dieser zusätzliche Betrag?", required: true, rows: 2, placeholder: "z. B. weiterer Sparplan, Bargeld-Rücklage, Tagesgeldkonto …" }),
        h("button", { type: "button", class: "btn-edit", text: "Weiteren Spartopf oben ergänzen", on: { click: () => ctx.goTo("vermoegen") } }),
      );
    };
    const sparHint = postenSumme > 0
      ? `Aus deinen oben erfassten Sparprodukten haben wir bereits ${formatEUR(postenSumme)} übernommen. Sparst du insgesamt mehr, trag hier den Gesamtbetrag ein.`
      : "Was du aktuell schon monatlich zur Seite legst – wird separat ausgewiesen.";

    const node = h("div", {},
      card(
        h("div", { class: "card__title", text: "Laufende Ausgaben (monatlich)" }),
        h("div", { class: "grid-2" },
          moneyInput(s, "ausgaben.wohnen", { label: "Warmmiete / Wohnkosten", required: true, hint: "Alle Kosten für deine Wohnung – inkl. Nebenkosten wie Heiz-, Wasser- und Stromkosten." }),
          moneyInput(s, "ausgaben.lebenshaltung", { label: "Lebenshaltungskosten", required: true, hint: "Lebensmittel und Getränke – alles für deinen täglichen Bedarf." })),
        h("div", { class: "grid-2" },
          moneyInput(s, "ausgaben.mobilitaet", { label: "Mobilität", required: true, hint: "Benzin, Bus & Bahn (z. B. BahnCard/Ticket), Auto- und Kfz-Kosten." }),
          moneyInput(s, "ausgaben.kreditraten", { label: "Kreditraten (gesamt)", required: true, hint: "Nur die tatsächliche monatliche Rate – ohne separate Tilgung." })),
        h("div", { class: "grid-2" },
          moneyInput(s, "ausgaben.unterhalt", { label: "Unterhalt", required: true, hint: "Unterhaltszahlungen, z. B. für Kinder oder Ex-Partner/in." }),
          moneyInput(s, "ausgaben.abosVereine", { label: "Abonnements & Vereine", required: true, hint: "Streaming, Handy, Fitnessstudio, Mitgliedsbeiträge usw." })),
        moneyInput(s, "ausgaben.sonstige", { label: "Sonstige Ausgaben", required: true, hint: "Freizeit- & Spaßausgaben – Kino, Essen gehen, Treffen mit Freunden, Feiern usw." }),
        moneyInput(s, "ausgaben.versicherungen", { label: "Versicherungsbeiträge", required: true, hint: versHint, onInput: renderVersSurplus }),
        versUebernahme,
        versSurplusBox
      ),
      card(
        h("div", { class: "card__title", text: "Bestehende Sparbeiträge" }),
        h("p", { class: "card__hint", text: sparHint }),
        moneyInput(s, "ausgaben.sparbeitraege", { label: "Monatliche Sparbeiträge (gesamt)", required: true, onInput: renderSparSurplus }),
        sparSurplusBox
      )
    );
    renderVersSurplus();
    renderSparSurplus();
    return node;
  },
};

/* ---------- 14 · Haushaltsübersicht ---------- */
export const stepHaushalt: Step = {
  id: "haushalt",
  section: "haushalt",
  eyebrow: "Haushaltscheck",
  title: "Deine Haushaltsübersicht",
  subtitle: "Live aus deinen Angaben berechnet.",
  build(ctx) {
    const s = ctx.store;
    const h2 = berechneHaushalt(s.state);
    const neg = h2.freiVerfuegbar < 0;

    const rc = (label: string, value: number, variant?: "accent" | "pos" | "neg") =>
      h("div", { class: `result-card${variant === "accent" ? " result-card--accent" : ""}` },
        h("div", { class: "result-card__label", text: label }),
        h("div", { class: `result-card__value${variant === "pos" ? " value--pos" : variant === "neg" ? " value--neg" : ""}`, text: formatEUR(value) }));

    return h("div", {},
      card(
        h("div", { class: "result-grid" },
          rc("Einnahmen gesamt", h2.einnahmenGesamt),
          rc("Laufende Verbindlichkeiten", h2.laufendeVerbindlichkeiten),
          rc(neg ? "Monatliche Unterdeckung" : "Frei verfügbarer Betrag", h2.freiVerfuegbar, neg ? "neg" : "pos")),
        h("p", { class: "field__optional", style: { marginTop: "12px", marginBottom: "0" }, text: "Laufende Verbindlichkeiten = deine Ausgaben inkl. bestehender Sparbeiträge." })
      ),
      neg
        ? note("warn-dark", "Aktuell liegen deine Ausgaben über deinen Einnahmen. Das ist kein Problem – wir schauen uns gemeinsam an, wo Spielraum entsteht.")
        : note("info-dark", "Sieht solide aus. Diesen frei verfügbaren Betrag nehmen wir gleich als Ausgangspunkt für deine Zukunftsplanung.")
    );
  },
};
